import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { FaTrash, FaEdit, FaEye, FaDownload, FaPlus, FaUpload, FaFileAlt, FaSignature, FaSyncAlt, FaCopy } from 'react-icons/fa';
import api from '../../services/api';
import { DocumentoLocacao, Locacao } from '../../services/tipos';
import { handleDownloadFile } from '../../services/utilitarios';
import { ContratoViewer } from '../docx-preview';

interface Props {
    show: boolean;
    locacao: Locacao | null;
    onClose: () => void;
}

const TIPO_CONTRATO = 'contrato';
const TIPO_EXTRA = 'documento_extra';
const LIMITE_EXTRAS = 3;
const TAMANHO_MAXIMO_BYTES = 10 * 1024 * 1024; // 10 MB
const MIMES_PERMITIDOS = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'image/jpeg',
    'image/jpg',
    'image/png',
];
const ACCEPT_INPUT = '.pdf,.docx,.doc,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png';

function formatarTamanho(bytes?: number | null): string {
    if (!bytes || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatarData(iso?: string): string {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
}

function validarArquivo(file: File): string | null {
    if (!file) return 'Selecione um arquivo.';
    if (file.size > TAMANHO_MAXIMO_BYTES) return 'Arquivo excede 10 MB.';
    if (!MIMES_PERMITIDOS.includes(file.type)) return 'Tipo de arquivo não permitido. Use PDF, DOCX, JPG ou PNG.';
    return null;
}

type Modo = 'lista' | 'novo' | 'editando';

export default function ModalDocumentosLocacao({ show, locacao, onClose }: Props) {
    const [modo, setModo] = useState<Modo>('lista');
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [documentos, setDocumentos] = useState<DocumentoLocacao[]>([]);

    // Form state (novo/editando)
    const [docEditando, setDocEditando] = useState<DocumentoLocacao | null>(null);
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [arquivo, setArquivo] = useState<File | null>(null);
    const [arrastando, setArrastando] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Confirmação de exclusão
    const [confirmandoExclusao, setConfirmandoExclusao] = useState<DocumentoLocacao | null>(null);
    const [senhaConfirmacao, setSenhaConfirmacao] = useState('');
    const [excluindo, setExcluindo] = useState(false);

    // Visualizador embutido (PDF/DOCX) — não expõe URL do MinIO
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerLink, setViewerLink] = useState('');
    const [viewerTitulo, setViewerTitulo] = useState('Documento');

    const [processandoAssinaturaId, setProcessandoAssinaturaId] = useState<string | null>(null);
    const [linkAssinatura, setLinkAssinatura] = useState<{ nomeDocumento: string; link: string } | null>(null);

    const carregarDocumentos = useCallback(async (idLocacao: string) => {
        setCarregando(true);
        try {
            const resp = await api.get<DocumentoLocacao[]>(`/locacao/${idLocacao}/documentos`);
            setDocumentos(resp.data || []);
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao carregar documentos.');
            setDocumentos([]);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        if (show && locacao?.id) {
            setModo('lista');
            setNome('');
            setDescricao('');
            setArquivo(null);
            setDocEditando(null);
            setConfirmandoExclusao(null);
            setSenhaConfirmacao('');
            carregarDocumentos(locacao.id);
        }
    }, [show, locacao, carregarDocumentos]);

    const qtdExtras = useMemo(() => documentos.filter(d => d.tipo === TIPO_EXTRA).length, [documentos]);
    const podeAdicionar = qtdExtras < LIMITE_EXTRAS;

    const handleAbrirNovo = () => {
        if (!podeAdicionar) {
            toast.warning(`Limite de ${LIMITE_EXTRAS} documentos extras atingido.`);
            return;
        }
        setDocEditando(null);
        setNome('');
        setDescricao('');
        setArquivo(null);
        setModo('novo');
    };

    const handleAbrirEdicao = (doc: DocumentoLocacao) => {
        if (doc.tipo === TIPO_CONTRATO) {
            toast.info('O contrato gerado pelo sistema não pode ser editado por aqui. Use a opção de gerar contrato para substituí-lo.');
            return;
        }
        setDocEditando(doc);
        setNome(doc.nomeOriginal || '');
        setDescricao(doc.descricao || '');
        setArquivo(null);
        setModo('editando');
    };

    const handleArquivoSelecionado = (file: File | null) => {
        if (!file) { setArquivo(null); return; }
        const err = validarArquivo(file);
        if (err) {
            toast.warning(err);
            return;
        }
        setArquivo(file);
    };

    const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setArrastando(true); };
    const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setArrastando(false); };
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setArrastando(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleArquivoSelecionado(f);
    };

    const handleSalvar = async () => {
        if (!locacao?.id) return;
        if (!nome.trim()) { toast.warning('Informe o nome do documento.'); return; }
        if (modo === 'novo' && !arquivo) { toast.warning('Selecione um arquivo.'); return; }

        setSalvando(true);
        try {
            const fd = new FormData();
            fd.append('nome', nome.trim());
            if (descricao.trim()) fd.append('descricao', descricao.trim());

            if (modo === 'novo') {
                fd.append('arquivo', arquivo!);
                await api.post(`/locacao/${locacao.id}/documentos`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success('Documento adicionado!');
            } else if (modo === 'editando' && docEditando) {
                if (arquivo) fd.append('arquivoNovo', arquivo);
                await api.put(`/locacao/${locacao.id}/documentos/${docEditando.id}`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success('Documento atualizado!');
            }

            await carregarDocumentos(locacao.id);
            setModo('lista');
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao salvar documento.');
        } finally {
            setSalvando(false);
        }
    };

    const handleConfirmarExclusao = async () => {
        if (!locacao?.id || !confirmandoExclusao) return;
        if (!senhaConfirmacao) { toast.warning('Digite sua senha.'); return; }
        setExcluindo(true);
        try {
            await api.delete(`/locacao/${locacao.id}/documentos/${confirmandoExclusao.id}`, {
                data: { senha: senhaConfirmacao },
            });
            toast.success('Documento excluído!');
            setConfirmandoExclusao(null);
            setSenhaConfirmacao('');
            await carregarDocumentos(locacao.id);
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao excluir documento.');
        } finally {
            setExcluindo(false);
        }
    };

    const handleVisualizar = (doc: DocumentoLocacao) => {
        if (!doc.urlAcesso) { toast.warning('URL não disponível.'); return; }
        // Renderiza inline via ContratoViewer (faz fetch+blob — não expõe a URL do MinIO no navegador)
        setViewerLink(doc.urlAcesso);
        setViewerTitulo(doc.nomeOriginal || 'Documento');
        setViewerOpen(true);
    };

    const handleBaixar = async (doc: DocumentoLocacao) => {
        if (!doc.urlAcesso) { toast.warning('URL não disponível.'); return; }
        // handleDownloadFile faz fetch → blob → download programático.
        // O usuário só vê o nome do arquivo, nunca a URL bruta.
        await handleDownloadFile(
            doc.urlAcesso,
            doc.nomeOriginal || 'documento',
            () => toast.warning('URL do documento não disponível.'),
            () => toast.success('Download iniciado.'),
            () => toast.error('Não foi possível baixar o documento.'),
        );
    };

    const handleGerarLinkAssinatura = async (doc: DocumentoLocacao) => {
        if (!locacao?.id) return;
        setProcessandoAssinaturaId(doc.id);
        try {
            const resp = await api.post<{ link: string }>(`/locacao/${locacao.id}/documentos/${doc.id}/assinatura/link`);
            setLinkAssinatura({ nomeDocumento: doc.nomeOriginal, link: resp.data.link });
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao gerar link de assinatura.');
        } finally {
            setProcessandoAssinaturaId(null);
        }
    };

    const handleAtualizarAssinado = async (doc: DocumentoLocacao) => {
        if (!locacao?.id) return;
        setProcessandoAssinaturaId(doc.id);
        try {
            await api.put(`/locacao/${locacao.id}/documentos/${doc.id}/assinatura/atualizar`);
            toast.success('Documento assinado atualizado!');
            await carregarDocumentos(locacao.id);
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao atualizar documento assinado.');
        } finally {
            setProcessandoAssinaturaId(null);
        }
    };

    const handleCopiarLink = async () => {
        if (!linkAssinatura) return;
        try {
            await navigator.clipboard.writeText(linkAssinatura.link);
            toast.success('Link copiado para a área de transferência!');
        } catch {
            toast.error('Não foi possível copiar o link.');
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
            <Modal.Header style={{ backgroundColor: '#1f467e' }} className="text-white border-0">
                <Modal.Title style={{ fontSize: 16, fontWeight: 600 }} className="d-flex align-items-center gap-2">
                    <FaFileAlt /> Documentos da Locação
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {modo === 'lista' && (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                {qtdExtras} de {LIMITE_EXTRAS} documentos extras utilizados
                                {documentos.some(d => d.tipo === TIPO_CONTRATO) && ' • contrato presente'}
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleAbrirNovo}
                                disabled={!podeAdicionar}
                                style={{ backgroundColor: '#1f467e', borderColor: '#1f467e' }}
                            >
                                <FaPlus className="me-1" /> Adicionar Novo
                            </Button>
                        </div>

                        {carregando && (
                            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                        )}

                        {!carregando && documentos.length === 0 && (
                            <Alert variant="light" className="text-center">
                                Nenhum documento cadastrado ainda.
                            </Alert>
                        )}

                        {!carregando && documentos.length > 0 && (
                            <div className="d-flex flex-column gap-2">
                                {documentos.map(doc => (
                                    <div key={doc.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2">
                                                    <strong style={{ color: '#1f467e' }}>{doc.nomeOriginal}</strong>
                                                    {doc.tipo === TIPO_CONTRATO && (
                                                        <span style={{ background: '#1f467e', color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                                                            CONTRATO
                                                        </span>
                                                    )}
                                                    {doc.tipo === TIPO_EXTRA && doc.autentiqueDocumentId && (
                                                        doc.assinadoEm ? (
                                                            <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                                                                ASSINADO
                                                            </span>
                                                        ) : (
                                                            <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                                                                AGUARDANDO ASSINATURA
                                                            </span>
                                                        )
                                                    )}
                                                </div>
                                                {doc.descricao && (
                                                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                                        {doc.descricao}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                                    {formatarTamanho(doc.tamanho)} • {formatarData(doc.criadoEm)}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1 flex-shrink-0">
                                                {doc.tipo === TIPO_EXTRA && doc.autentiqueDocumentId && !doc.assinadoEm && (
                                                    <>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            title="Assinar e copiar link para o locatário"
                                                            onClick={() => handleGerarLinkAssinatura(doc)}
                                                            disabled={processandoAssinaturaId === doc.id}
                                                        >
                                                            <FaSignature />
                                                        </Button>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            title="Atualizar documento assinado"
                                                            onClick={() => handleAtualizarAssinado(doc)}
                                                            disabled={processandoAssinaturaId === doc.id}
                                                        >
                                                            {processandoAssinaturaId === doc.id ? <Spinner animation="border" size="sm" /> : <FaSyncAlt />}
                                                        </Button>
                                                    </>
                                                )}
                                                <Button variant="outline-primary" size="sm" title="Visualizar" onClick={() => handleVisualizar(doc)}>
                                                    <FaEye />
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" title="Baixar" onClick={() => handleBaixar(doc)}>
                                                    <FaDownload />
                                                </Button>
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    title={doc.tipo === TIPO_CONTRATO ? 'Contrato gerado não pode ser editado por aqui' : 'Editar'}
                                                    onClick={() => handleAbrirEdicao(doc)}
                                                    disabled={doc.tipo === TIPO_CONTRATO}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button variant="outline-danger" size="sm" title="Excluir" onClick={() => setConfirmandoExclusao(doc)}>
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {(modo === 'novo' || modo === 'editando') && (
                    <>
                        <h6 style={{ color: '#1f467e' }}>{modo === 'novo' ? 'Adicionar Novo Documento' : 'Editar Documento'}</h6>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Nome *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    maxLength={200}
                                    placeholder="Ex: Aditivo de prorrogação"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Descrição (opcional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    maxLength={500}
                                    placeholder="Detalhes do documento..."
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                                    Arquivo {modo === 'novo' ? '*' : '(envie um novo apenas se desejar substituir)'}
                                </Form.Label>
                                <div
                                    onDragOver={onDragOver}
                                    onDragLeave={onDragLeave}
                                    onDrop={onDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        border: `2px dashed ${arrastando ? '#1f467e' : '#d1d5db'}`,
                                        borderRadius: 8,
                                        padding: 24,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: arrastando ? '#eff6ff' : '#fafafa',
                                    }}
                                >
                                    <FaUpload style={{ fontSize: 24, color: '#9ca3af', marginBottom: 8 }} />
                                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                                        {arquivo
                                            ? <strong>{arquivo.name} ({formatarTamanho(arquivo.size)})</strong>
                                            : <>Arraste um arquivo aqui ou clique para selecionar</>
                                        }
                                    </div>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                        PDF, DOCX, JPG, PNG • máx 10 MB
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPT_INPUT}
                                    onChange={(e) => handleArquivoSelecionado(e.target.files?.[0] ?? null)}
                                    style={{ display: 'none' }}
                                />
                            </Form.Group>
                        </Form>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between" style={{ borderTop: '1px solid #e5e7eb' }}>
                {modo === 'lista' ? (
                    <Button variant="outline-secondary" onClick={onClose}>Fechar</Button>
                ) : (
                    <>
                        <Button variant="outline-secondary" onClick={() => setModo('lista')} disabled={salvando}>
                            Voltar
                        </Button>
                        <Button
                            onClick={handleSalvar}
                            disabled={salvando}
                            style={{ backgroundColor: '#1f467e', borderColor: '#1f467e', fontWeight: 600 }}
                        >
                            {salvando ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : 'Salvar'}
                        </Button>
                    </>
                )}
            </Modal.Footer>

            {/* Modal de confirmação de exclusão */}
            <Modal show={!!confirmandoExclusao} onHide={() => { setConfirmandoExclusao(null); setSenhaConfirmacao(''); }} centered backdrop="static">
                <Modal.Header className="bg-danger text-white">
                    <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>Confirmar exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: 14 }}>
                        Tem certeza que deseja excluir <strong>{confirmandoExclusao?.nomeOriginal}</strong>?<br />
                        Essa ação é <strong>irreversível</strong>.
                    </p>
                    <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Digite sua senha para confirmar:</Form.Label>
                        <Form.Control
                            type="password"
                            autoFocus
                            value={senhaConfirmacao}
                            onChange={(e) => setSenhaConfirmacao(e.target.value)}
                            placeholder="Senha"
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter' && senhaConfirmacao) handleConfirmarExclusao(); }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => { setConfirmandoExclusao(null); setSenhaConfirmacao(''); }} disabled={excluindo}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleConfirmarExclusao} disabled={excluindo || !senhaConfirmacao}>
                        {excluindo ? <><Spinner animation="border" size="sm" className="me-2" />Excluindo...</> : 'Excluir'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!linkAssinatura} onHide={() => setLinkAssinatura(null)} centered>
                <Modal.Header style={{ backgroundColor: '#1f467e' }} className="text-white border-0">
                    <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>Link para Assinatura</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: 13, color: '#374151' }}>
                        Copie o link abaixo e envie para o locatário assinar <strong>{linkAssinatura?.nomeDocumento}</strong>.
                    </p>
                    <Form.Control type="text" readOnly value={linkAssinatura?.link ?? ''} onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setLinkAssinatura(null)}>Fechar</Button>
                    <Button onClick={handleCopiarLink} style={{ backgroundColor: '#1f467e', borderColor: '#1f467e', fontWeight: 600 }}>
                        <FaCopy className="me-1" /> Copiar Link
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Visualizador de PDF/DOCX renderizado inline (não expõe URL do MinIO) */}
            <ContratoViewer
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                link={viewerLink}
                titulo={viewerTitulo}
            />
        </Modal>
    );
}
